import {
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  Calculator,
  Cpu,
  CreditCard,
  DollarSign,
  FileArchive,
  FileSearch,
  FileSpreadsheet,
  FileText,
  Handshake,
  HeartHandshake,
  History,
  KeyRound,
  LayoutDashboard,
  Mail,
  MessageSquareWarning,
  Package,
  Percent,
  Plug,
  Receipt,
  Scale,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";

import { MODULES, type ModuleKey } from "./modules";

/**
 * Sidebar entries that belong to a module. The entry is hidden when the
 * module is off for this edition, on top of the role permission check.
 */
export const navbarItemFeatures: Record<string, ModuleKey> = {
  "lovable-rfqs": MODULES.RFQ_TO_MARKET,
  "lovable-commissions": MODULES.COMMISSIONS,
  "lovable-commission-ledger": MODULES.COMMISSIONS,
  "lovable-invoices": MODULES.INVOICING,
  "report-templates": MODULES.UNDERWRITING_DOSSIER,
  products: MODULES.PRODUCTS,
  submissions: MODULES.SUBMISSIONS,
  underwriting: MODULES.UNDERWRITING,
  approvals: MODULES.UNDERWRITING,
  authority: MODULES.UNDERWRITING,
  partners: MODULES.PARTNERS,
  "my-organisation": MODULES.PARTNERS,
  "commission-plans": MODULES.PAYOUTS,
  payouts: MODULES.PAYOUTS,
  earnings: MODULES.PAYOUTS,
  "rate-cards": MODULES.RATING,
  analytics: MODULES.ANALYTICS,
  receivables: MODULES.RECEIVABLES,
  bordereaux: MODULES.RECEIVABLES,
  payments: MODULES.PAYMENTS,
  grievances: MODULES.COMPLIANCE,
  "data-exports": MODULES.DATA_EXPORTS,
  integrations: MODULES.INTEGRATIONS,
  benefits: MODULES.GROUP_BENEFITS,
  "employer-home": MODULES.GROUP_BENEFITS,
  "employer-members": MODULES.GROUP_BENEFITS,
  "employer-approvals": MODULES.GROUP_BENEFITS,
  "employer-uploads": MODULES.GROUP_BENEFITS,
  "employer-changes": MODULES.GROUP_BENEFITS,
  "employer-billing": MODULES.GROUP_BENEFITS,
  "employer-settings": MODULES.GROUP_BENEFITS,
  "my-cover": MODULES.GROUP_BENEFITS,
  "my-family": MODULES.GROUP_BENEFITS,
  "my-options": MODULES.GROUP_BENEFITS,
  "my-requests": MODULES.GROUP_BENEFITS,
  "my-profile": MODULES.GROUP_BENEFITS,
};

/** Entries only insurer (root) staff should see, whatever their role grants. */
export const rootOnlyNavItems = new Set([
  "underwriting",
  "approvals",
  "authority",
  "partners",
  "commission-plans",
  "payouts",
  "rate-cards",
  "receivables",
  "bordereaux",
  "data-exports",
  "integrations",
  "roles",
  "org-profile",
  "notification-templates",
  "form-config",
  "report-templates",
  "processing-engines",
  "data-providers",
  "settings",
  // "team" is deliberately not here: a partner administrator manages their
  // own users through it, and the API limits what they see and may grant.
  "audit",
]);

/** Entries only channel-partner users should see. */
export const partnerOnlyNavItems = new Set(["my-organisation", "earnings"]);

/** Entries only an employer's HR should see (Module 26, employer portal). */
export const employerOnlyNavItems = new Set<string>([
  "employer-home",
  "employer-members",
  "employer-approvals",
  "employer-uploads",
  "employer-changes",
  "employer-billing",
  "employer-settings",
]);

/** Entries only a covered employee should see (Module 26, employee portal). */
export const employeeOnlyNavItems = new Set<string>([
  "my-cover",
  "my-family",
  "my-options",
  "my-requests",
  "my-profile",
]);

/** Legacy broker pages (external iframes) that have no place in the insurer edition. */
// Broker-era screens: the insurer edition settles partner commission through Payouts and Earnings.
export const brokerOnlyNavItems = new Set([
  "Client",
  "lovable-tax",
  "lovable-knowledge",
  "lovable-commissions",
  "lovable-commission-ledger",
]);

export const navbarItems = [
  {
    id: "Dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    module: "dashboard",
    path: "/dashboard",
    action: "view",
  },

  {
    id: "Pipeline",
    label: "Pipeline",
    icon: BarChart3,
    module: "pipeline",
    path: "/pipeline-analytics",
    action: "view",
  },

  {
    id: "lovable-leads",
    label: "Leads/Client",
    icon: Target,
    module: "client",
    path: "/leads",
    action: "view",
    // isLovable: true,
    // group: "Pipeline",
  },
  // {
  //   id: "Client",
  //   label: "Clients",
  //   icon: Users,
  //   module: "clients",
  //   path: "/clients",
  //   action: "list",
  // },

  {
    id: "lovable-opportunities",
    label: "Opportunities",
    icon: TrendingUp,
    module: "opportunities",
    path: "/opportunities",
    action: "view",
    isLovable: true,
    group: "Pipeline",
  },

  {
    id: "lovable-rfqs",
    label: "RFQs",
    icon: FileText,
    module: "rfqs",
    path: "/rfqs",
    action: "view",
    isLovable: true,
    group: "Quoting",
  },

  {
    id: "Policies",
    label: "Policies",
    icon: ShieldCheck,
    module: "policies",
    path: "/policies",
    action: "view",
  },

  {
    id: "lovable-commissions",
    label: "Commissions",
    icon: DollarSign,
    module: "commissions",
    path: "/commissions",
    action: "view",
    isLovable: true,
    group: "Money",
  },

  {
    id: "lovable-commission-ledger",
    label: "Commission Ledger",
    icon: Package,
    module: "commission_ledger",
    path: "/commissions-ledger",
    action: "view",
    isLovable: true,
    group: "Money",
  },

  {
    id: "lovable-invoices",
    label: "Invoice",
    icon: FileText,
    module: "invoice",
    path: "/invoices",
    action: "view",
    isLovable: true,
    group: "Money",
  },

  {
    id: "lovable-tax",
    label: "Tax & Statutory",
    icon: Receipt,
    module: "tax_and_statutory",
    path: "/tax",
    action: "view",
    isLovable: true,
    group: "Money",
  },

  {
    id: "lovable-knowledge",
    label: "Knowledge Library",
    icon: BookOpen,
    // "quotes" is a module the role has; "catalog" has no permission so the item would be hidden.
    module: "quotes",
    path: "/knowledge",
    action: "view",
    isLovable: true,
    group: "Catalog",
  },

  {
    id: "form-config",
    label: "Form Config",
    icon: Package,
    module: "form-config",
    path: "/form-config",
    action: "view",
  },

  {
    id: "report-templates",
    label: "Dossier Templates",
    icon: FileSearch,
    module: "report-templates",
    path: "/report-templates",
    action: "view",
  },

  {
    id: "processing-engines",
    label: "AI Engines",
    icon: Cpu,
    module: "processing-engines",
    path: "/processing-engines",
    action: "view",
  },

  {
    id: "data-providers",
    label: "Data Services",
    icon: Plug,
    module: "data-providers",
    path: "/data-providers",
    action: "view",
  },

  {
    id: "settings",
    label: "Settings",
    icon: SlidersHorizontal,
    module: "settings",
    path: "/settings",
    action: "view",
  },

  {
    id: "products",
    label: "Products",
    icon: Package,
    module: "products",
    path: "/products",
    action: "list",
  },
  // Submissions have no menu entry of their own: a requester follows a case
  // from the Opportunities list (Underwriting column) and the opportunity's
  // Underwriting tab; underwriters work from the Underwriting inbox.
  {
    id: "underwriting",
    label: "Underwriting",
    icon: Briefcase,
    module: "underwriting",
    path: "/underwriting",
    action: "list",
  },
  {
    id: "approvals",
    label: "Approvals",
    icon: ShieldCheck,
    module: "approvals",
    path: "/underwriting/approvals",
    action: "list",
  },
  {
    id: "authority",
    label: "Authority limits",
    icon: Scale,
    module: "authority",
    path: "/underwriting/authority",
    action: "list",
  },
  {
    id: "partners",
    label: "Channel partners",
    icon: Handshake,
    module: "partners",
    path: "/partners",
    action: "list",
  },
  {
    id: "benefits",
    label: "Group benefits",
    icon: HeartHandshake,
    module: "benefits",
    path: "/benefits",
    action: "list",
  },
  // Module 26, Phase C: the employer portal (HR) and the employee portal.
  {
    id: "employer-home",
    label: "Home",
    icon: LayoutDashboard,
    module: "benefits-self",
    path: "/employer",
    action: "manage",
  },
  {
    id: "employer-members",
    label: "Employees",
    icon: Users,
    module: "benefits-self",
    path: "/employer/members",
    action: "manage",
  },
  {
    id: "employer-approvals",
    label: "Approvals",
    icon: ShieldCheck,
    module: "benefits-self",
    path: "/employer/approvals",
    action: "approve",
  },
  {
    id: "employer-uploads",
    label: "Uploads",
    icon: FileSpreadsheet,
    module: "benefits-self",
    path: "/employer/uploads",
    action: "import",
  },
  {
    id: "employer-changes",
    label: "Changes",
    icon: History,
    module: "benefits-self",
    path: "/employer/changes",
    action: "manage",
  },
  {
    id: "employer-billing",
    label: "Billing",
    icon: Receipt,
    module: "benefits-self",
    path: "/employer/billing",
    action: "billing",
  },
  {
    id: "employer-settings",
    label: "Portal settings",
    icon: SlidersHorizontal,
    module: "benefits-self",
    path: "/employer/settings",
    action: "manage",
  },
  {
    id: "my-cover",
    label: "My cover",
    icon: HeartHandshake,
    module: "benefits-self",
    path: "/me/cover",
    action: "view",
  },
  {
    id: "my-family",
    label: "My family",
    icon: Users,
    module: "benefits-self",
    path: "/me/family",
    action: "view",
  },
  {
    id: "my-options",
    label: "Optional covers",
    icon: Package,
    module: "benefits-self",
    path: "/me/options",
    action: "view",
  },
  {
    id: "my-requests",
    label: "My requests",
    icon: FileText,
    module: "benefits-self",
    path: "/me/requests",
    action: "view",
  },
  {
    id: "my-profile",
    label: "My profile",
    icon: KeyRound,
    module: "benefits-self",
    path: "/me/profile",
    action: "view",
  },
  {
    id: "my-organisation",
    label: "My organisation",
    icon: Building2,
    module: "submissions",
    path: "/my-organisation",
    action: "list",
  },
  {
    id: "commission-plans",
    label: "Commission plans",
    icon: Percent,
    module: "commission-plans",
    path: "/commission-plans",
    action: "list",
  },
  {
    id: "payouts",
    label: "Payouts",
    icon: Wallet,
    module: "payouts",
    path: "/payouts",
    action: "list",
  },
  {
    id: "earnings",
    label: "My earnings",
    icon: Wallet,
    module: "earnings",
    path: "/earnings",
    action: "list",
  },
  {
    id: "rate-cards",
    label: "Rate cards",
    icon: Calculator,
    module: "rate-cards",
    path: "/rate-cards",
    action: "list",
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: BarChart3,
    module: "analytics",
    path: "/analytics",
    action: "view",
  },
  {
    id: "receivables",
    label: "Receivables",
    icon: Receipt,
    module: "receivables",
    path: "/receivables",
    action: "list",
  },
  {
    id: "payments",
    label: "Payments",
    icon: CreditCard,
    module: "payments",
    path: "/payments",
    action: "list",
  },
  {
    id: "bordereaux",
    label: "Bordereaux",
    icon: FileSpreadsheet,
    module: "receivables",
    path: "/bordereaux",
    action: "list",
  },
  {
    id: "grievances",
    label: "Grievances",
    icon: MessageSquareWarning,
    module: "grievances",
    path: "/grievances",
    action: "list",
  },
  {
    id: "data-exports",
    label: "Data Exports",
    icon: FileArchive,
    module: "data-exports",
    path: "/data-exports",
    action: "list",
  },
  {
    id: "integrations",
    label: "Integrations",
    icon: Plug,
    module: "integrations",
    path: "/integrations",
    action: "list",
  },
  { id: "roles", label: "Roles", icon: KeyRound, module: "roles", path: "/roles", action: "list" },
  {
    id: "org-profile",
    label: "Organisation",
    icon: Building2,
    module: "org-profile",
    path: "/organisation",
    action: "view",
  },
  {
    id: "notification-templates",
    label: "Notification templates",
    icon: Mail,
    module: "notifications",
    path: "/notification-templates",
    action: "list",
  },

  {
    id: "team",
    label: "Team",
    icon: UserPlus,
    module: "users",
    path: "/team",
    action: "list",
  },

  {
    id: "audit",
    label: "Audit Trail",
    icon: History,
    module: "audit",
    path: "/audit",
    action: "list",
  },
];
